import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { WidgetRendererComponent } from '../widget-renderer/widget-renderer.component';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from '../../../../my-shared-lib/src/lib/shared-services/config.service';
import { GlobalFilterComponent } from '../../../../my-shared-lib/src/lib/global-components/global-filter/global-filter.component';
import { distinctUntilChanged, forkJoin, Subscription } from 'rxjs';
import { GlobalFilterService } from '../../../../my-shared-lib/src/lib/shared-services/global-filter-service';
import { GlobalService } from '../../../../my-shared-lib/src/lib/shared-services/global.service';
import { CustomFilterComponent } from '../../../../my-shared-lib/src/lib/global-components/custom-filter/custom-filter.component';
import { ButtonComponent } from '../../../../my-shared-lib/src/lib/global-components/button/button.component';
import { LegendTableComponent } from '../../../../my-shared-lib/src/lib/global-components/legend-table/legend-table.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';


@Component({
  selector: 'app-dashboard',
  imports: [
    HttpClientModule,
    WidgetRendererComponent,
    CommonModule,
    GlobalFilterComponent,
    CustomFilterComponent,
    ButtonComponent,
    LegendTableComponent,
    MatIconModule,
    MatTooltipModule,
   // NotesComponent,
  ],
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
  private readonly productRoadmapBodyClass = 'product-roadmap-scroll-unlock';
  private readonly defaultPlanningYearFilterKey = 'ecosystem_active_year';
  configurations: any = {};
  dynamicSummaryTitle = '';
  showImplementationNotes = false;
  dashboardType: any;
  @ViewChildren('chartContainer', { read: ViewContainerRef })
  chartContainers!: QueryList<ViewContainerRef>;
  private subscriptions = new Subscription();

  globalDropdownConfig: any;
  selectedYear: number | null = null;
  private planningYearFilterKey = this.defaultPlanningYearFilterKey;
  configFileName!: string;
  clickedWidgetId: string | null = null;
  isProductRoadmapLoading = false;
  private spendAllocationRequestTimer: any = null;

  constructor(
    public configService: ConfigService,
    private route: ActivatedRoute,
    private globalFilterService: GlobalFilterService,
    private service: GlobalService
  ) {}

  ngOnInit() {
    this.subscriptions.add(
      this.configService.bubbleClick$.subscribe((widgetId) => {
        this.clickedWidgetId = widgetId;
      })
    );
    const routeDataSub = this.route.data
      .pipe(distinctUntilChanged((previous, current) => previous?.['configFile'] === current?.['configFile']))
      .subscribe((data) => {
        if (data['configFile']) {
          this.configFileName = data['configFile'];
        }

        this.configurations = {};
        this.clickedWidgetId = null;
        this.configService.resetDrilldownState();
        this.resetAdvancedSimulationState();

        if (this.isProductRoadmapPage()) {
          this.isProductRoadmapLoading = true;
          this.service.setProductRoadmapLoading(true);
          const roadmapLoadingSub = this.service.productRoadmapLoading$.subscribe((isLoading) => {
            this.isProductRoadmapLoading = isLoading;
          });
          this.subscriptions.add(roadmapLoadingSub);
        }

        this.syncPageBodyClass();
        this.globalFilterService.updateAllFilters({
          queryParams: {},
          requestParams: {},
          widgetId: null,
        });
        this.loadCharts();
      });
    this.subscriptions.add(routeDataSub);

    const filterSub = this.service.filters$.subscribe((payload: any) => {
      this.scheduleSpendAllocationFetch(payload?.filter || {});
    });
    this.subscriptions.add(filterSub);
  }

  loadCharts() {
    const configSub = this.configService
      .getDashboardConfig(this.configFileName)
      .subscribe({
        next: (response) => {
          if (response.clearGlobalFilters) {
            this.service.resetFilterSelection(true, true);
          } else if (!response.keepFilters) {
            this.service.resetFilterSelection(true);
          }
          this.configurations = response;
          this.initializePlanningYearSelection();
          this.initializeDynamicSummaryTitle();
          this.updateImplementationNotesVisibility();
        },
        error: () => {
          if (this.isProductRoadmapPage()) {
            this.service.setProductRoadmapLoading(false);
            this.isProductRoadmapLoading = false;
          }
        },
      });
    this.subscriptions.add(configSub);
  }

  private initializePlanningYearSelection(): void {
    const selector = this.configurations?.planningYearSelector;
    if (!selector?.years?.length) {
      return;
    }

    this.planningYearFilterKey = String(
      selector?.requestKey || this.defaultPlanningYearFilterKey
    ).trim() || this.defaultPlanningYearFilterKey;
    const configuredDefaultYear = Number(selector.defaultYear);
    const defaultYear = selector.years
      .map((year: any) => Number(year))
      .find((year: number) =>
        Number.isFinite(year) && year === configuredDefaultYear
      ) ?? null;

    this.selectedYear = defaultYear;
    selector.selectedYear = defaultYear;
    this.pushPlanningYearSelection(defaultYear);
  }

  selectPlanningYear(year: number): void {
    if (!Number.isFinite(year)) {
      return;
    }

    if (this.selectedYear === year) {
      this.selectedYear = null;
      if (this.configurations?.planningYearSelector) {
        this.configurations.planningYearSelector.selectedYear = null;
      }
      this.pushPlanningYearSelection(null);
      return;
    }

    this.selectedYear = year;
    if (this.configurations?.planningYearSelector) {
      this.configurations.planningYearSelector.selectedYear = year;
    }
    this.pushPlanningYearSelection(year);
  }

  private pushPlanningYearSelection(year: number | null): void {
    this.service.updateFilterSelection(
      {
        [this.planningYearFilterKey]: year == null ? [] : [year],
      },
      ''
    );
  }

  private scheduleSpendAllocationFetch(filters: any): void {
    if (!this.isSpendAllocationPage() || !this.configurations?.pageContent) {
      return;
    }

    clearTimeout(this.spendAllocationRequestTimer);
    this.spendAllocationRequestTimer = setTimeout(() => {
      this.loadSpendAllocationBars(filters);
    }, 250);
  }

  private isSpendAllocationPage(): boolean {
    return this.configFileName === 'product-imprvmnt/spend-allocation.json';
  }

  private resetAdvancedSimulationState(): void {
    if (this.configFileName !== 'product-health/simulate2.json') {
      return;
    }

    this.clickedWidgetId = null;
    this.configService.setBubbleClicked('');
    this.configService.setDrilldownTitle('');
    this.configService.setDrilldownData(null);
    this.configService.setSimulatedData(null);

    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('filteredData');
    }
  }

  private toArray(values: any): string[] {
    if (Array.isArray(values)) {
      const flattened = values.flat(Infinity);
      return flattened
        .map((v: any) => String(v).trim())
        .filter((v: string) => !!v);
    }
    if (values == null || values === '') {
      return [];
    }
    return [String(values).trim()].filter((v) => !!v);
  }

  private loadSpendAllocationBars(activeFilters: any): void {
    if (!this.isSpendAllocationPage()) {
      return;
    }

    const quarterYear = this.toArray(activeFilters?.quarter_year);
    const portfolio = this.toArray(activeFilters?.product_portfolio);
    const finalEm = this.toArray(activeFilters?.final_em);

    const filters = {
      quarter_year: quarterYear,
      portfolio,
      final_em: finalEm,
    };

    forkJoin({
      actualRes: this.configService.fetchData(
        '/spriced/platform',
        'SpendAllocationService',
        filters,
        {}
      ),
      plannedRes: this.configService.fetchData(
        '/spriced/platform',
        'PlannedSpendAllocationService',
        filters,
        {}
      ),
    }).subscribe({
      next: ({ actualRes, plannedRes }) => {
        const actualRows = this.normalizeSpendAllocationRows(actualRes, 'actual');
        const plannedRows = this.normalizeSpendAllocationRows(plannedRes, 'planned');
        const merged = this.mergeSpendAllocationRows(actualRows, plannedRows);
        this.applySpendAllocationRowsToWidgets(merged);
      },
      error: () => {},
    });
  }

  private normalizeSpendAllocationRows(
    response: any,
    mode: 'actual' | 'planned'
  ): any[] {
    const payloadRows =
      response?.responseList?.[0]?.responseData ??
      response?.responseData ??
      response?.responseList?.[0]?.responseData?.[0] ??
      response;

    const rows = Array.isArray(payloadRows) ? payloadRows : [];

    return rows.map((row: any) => {
      const type = row?.type ?? row?.portfolio ?? row?.product_portfolio ?? '';
      const model =
        row?.model ?? row?.productId ?? row?.final_em ?? row?.platform ?? '';
      const ctActual =
        row?.actual?.ct ??
        row?.ct_actual ??
        row?.ct ??
        row?.customerTailoring ??
        row?.customer_tailoring;
      const sourcingActual =
        row?.actual?.sourcing ?? row?.sourcing_actual ?? row?.sourcing;
      const mfgActual =
        row?.actual?.mfg ?? row?.mfg_actual ?? row?.mfg ?? row?.manufacturing;
      const qualityActual = row?.actual?.quality ?? row?.quality_actual ?? row?.quality;
      const totalSpend =
        row?.totalSpend ??
        row?.total_spend ??
        row?.total_spend_mn ??
        row?.spend ??
        0;
      return {
        model,
        type,
        totalSpend: Number(totalSpend ?? 0),
        actual: {
          ct: Number(mode === 'actual' ? ctActual ?? 0 : 0),
          sourcing: Number(mode === 'actual' ? sourcingActual ?? 0 : 0),
          mfg: Number(mode === 'actual' ? mfgActual ?? 0 : 0),
          quality: Number(mode === 'actual' ? qualityActual ?? 0 : 0),
        },
        planned: {
          ct: Number(
            row?.planned?.ct ??
              row?.ct_planned ??
              (mode === 'planned' ? ctActual ?? 0 : 0)
          ),
          sourcing: Number(
            row?.planned?.sourcing ??
              row?.sourcing_planned ??
              (mode === 'planned' ? sourcingActual ?? 0 : 0)
          ),
          mfg: Number(
            row?.planned?.mfg ??
              row?.mfg_planned ??
              (mode === 'planned' ? mfgActual ?? 0 : 0)
          ),
          quality: Number(
            row?.planned?.quality ??
              row?.quality_planned ??
              (mode === 'planned' ? qualityActual ?? 0 : 0)
          ),
        },
      };
    });
  }

  private mergeSpendAllocationRows(actualRows: any[], plannedRows: any[]): any[] {
    const byKey = new Map<string, any>();
    const plannedByType = new Map<string, any>();

    actualRows.forEach((row) => {
      const key = `${String(row?.model || '').toLowerCase()}|${String(
        row?.type || ''
      ).toLowerCase()}`;
      byKey.set(key, { ...row });
    });

    plannedRows.forEach((row) => {
      const typeKey = String(row?.type || '').toLowerCase();
      const hasModel = String(row?.model || '').trim().length > 0;
      if (!hasModel && typeKey) {
        plannedByType.set(typeKey, {
          ct: Number(row?.planned?.ct ?? 0),
          sourcing: Number(row?.planned?.sourcing ?? 0),
          mfg: Number(row?.planned?.mfg ?? 0),
          quality: Number(row?.planned?.quality ?? 0),
        });
        return;
      }

      const key = `${String(row?.model || '').toLowerCase()}|${String(
        row?.type || ''
      ).toLowerCase()}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.totalSpend = Number(
          existing?.totalSpend ?? row?.totalSpend ?? 0
        );
        existing.planned = {
          ct: Number(row?.planned?.ct ?? existing?.planned?.ct ?? 0),
          sourcing: Number(row?.planned?.sourcing ?? existing?.planned?.sourcing ?? 0),
          mfg: Number(row?.planned?.mfg ?? existing?.planned?.mfg ?? 0),
          quality: Number(row?.planned?.quality ?? existing?.planned?.quality ?? 0),
        };
      } else {
        byKey.set(key, row);
      }
    });

    return Array.from(byKey.values()).map((row) => {
      const typeKey = String(row?.type || '').toLowerCase();
      const typePlan = plannedByType.get(typeKey);
      if (!typePlan) {
        return row;
      }
      return {
        ...row,
        planned: {
          ct: Number(typePlan.ct ?? row?.planned?.ct ?? 0),
          sourcing: Number(typePlan.sourcing ?? row?.planned?.sourcing ?? 0),
          mfg: Number(typePlan.mfg ?? row?.planned?.mfg ?? 0),
          quality: Number(typePlan.quality ?? row?.planned?.quality ?? 0),
        },
      };
    });
  }

  private applySpendAllocationRowsToWidgets(rows: any[]): void {
    const widgets = this.collectWidgets(this.configurations?.pageContent);
    if (!widgets.length) {
      return;
    }

    const byType: Record<string, any[]> = {
      Growth: rows.filter((r) => String(r?.type).toLowerCase() === 'growth'),
      'Core/Niche': rows.filter(
        (r) => String(r?.type).toLowerCase() === 'core/niche'
      ),
      'Harvest/Transition': rows.filter(
        (r) => String(r?.type).toLowerCase() === 'harvest/transition'
      ),
    };

    widgets.forEach((widget) => {
      const title = String(widget?.title || '').toLowerCase();
      if (title.includes('growth')) {
        widget.rawData = byType['Growth'];
        return;
      }
      if (title.includes('core/niche')) {
        widget.rawData = byType['Core/Niche'];
        return;
      }
      if (title.includes('harvest/transition')) {
        widget.rawData = byType['Harvest/Transition'];
      }
    });
  }

  private collectWidgets(items: any): any[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.flatMap((item: any) => {
      const current =
        item?.type === 'horizontal-grouped-bar-chart' ? [item] : [];
      return [
        ...current,
        ...this.collectWidgets(item?.columns),
        ...this.collectWidgets(item?.rows),
      ];
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.spendAllocationRequestTimer);
    this.removePageBodyClass();
    this.subscriptions.unsubscribe();
    this.globalFilterService.clearAllFilters();
    this.globalFilterService.activeFilter = null;
  }
  resetSelection() {
    this.clickedWidgetId = null;
    this.configService.setBubbleClicked('');
    this.service.resetNotify();
  }

  isProductRoadmapPage(): boolean {
    return this.configFileName === 'ideation/product-roadmap.json';
  }

  private syncPageBodyClass(): void {
    if (this.isProductRoadmapPage()) {
      this.addPageBodyClass();
      return;
    }
    this.removePageBodyClass();
  }

  private addPageBodyClass(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.body.classList.add(this.productRoadmapBodyClass);
  }

  private removePageBodyClass(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.body.classList.remove(this.productRoadmapBodyClass);
  }

  showSummaryDynamicTitle(): boolean {
    return this.isProductRoadmapPage() && this.configurations?.dynamicTitle?.showInSummaryRow === true;
  }

  resolveSummaryTitleClass(): string {
    return String(
      this.configurations?.dynamicTitle?.class || 'col-12 col-md-12 col-lg-4 mb-2 d-flex align-items-center',
    );
  }

  private initializeDynamicSummaryTitle(): void {
    const dynamicTitleConfig = this.configurations?.dynamicTitle;
    if (!this.showSummaryDynamicTitle()) {
      this.dynamicSummaryTitle = '';
      return;
    }

    const fallback = String(
      dynamicTitleConfig?.fallback || this.configurations?.['dashboard-name'] || '',
    ).trim();
    this.dynamicSummaryTitle = fallback;

    if (!dynamicTitleConfig?.apiUrl || !dynamicTitleConfig?.requestPayload) {
      return;
    }

    const method: 'post' | 'get' = dynamicTitleConfig?.method === 'get' ? 'get' : 'post';
    const titleSub = this.configService
      .customApi(
        dynamicTitleConfig.apiUrl,
        dynamicTitleConfig.requestPayload,
        dynamicTitleConfig,
        method,
      )
      .subscribe({
        next: (response) => {
          const mappedTitle = this.mapDynamicTitleFromResponse(response, dynamicTitleConfig);
          if (mappedTitle) {
            this.dynamicSummaryTitle = mappedTitle;
          }
        },
        error: () => {
          this.dynamicSummaryTitle = fallback;
        },
      });

    this.subscriptions.add(titleSub);
  }

  private mapDynamicTitleFromResponse(response: unknown, config: any): string {
    const source = config?.responsePath ? this.byPath(response, String(config.responsePath)) : response;
    let record: any = source;

    if (Array.isArray(source)) {
      const configuredIndex = Number(config?.summaryIndex);
      if (Number.isInteger(configuredIndex) && configuredIndex >= 0 && configuredIndex < source.length) {
        record = source[configuredIndex];
      } else {
        record = source[0];
      }
    }

    const titlePath = String(config?.titlePath || config?.key || '').trim();
    let rawValue: unknown = titlePath ? this.byPath(record, titlePath) : record;
    if (Array.isArray(rawValue)) {
      rawValue = rawValue[0];
    }

    const title = String(rawValue ?? '').trim();
    if (!title) {
      return '';
    }

    const prefix = String(config?.prefix || '').trim();
    const suffix = String(config?.suffix || '').trim();
    return [prefix, title, suffix].filter((part) => !!part).join(' ');
  }

  private byPath(source: any, path: string): any {
    return String(path)
      .split('.')
      .reduce(
        (current: any, key: string) =>
          current == null ? undefined : /^\d+$/.test(key) ? current[Number(key)] : current[key],
        source,
      );
  }

  private updateImplementationNotesVisibility(): void {
    this.showImplementationNotes =
      this.hasGanttChartWidget(this.configurations?.pageContent) ||
      this.hasGanttChartWidget(this.configurations?.leftSidebarWidgets);
  }

  private hasGanttChartWidget(items: any): boolean {
    if (!Array.isArray(items) || items.length === 0) {
      return false;
    }

    return items.some((item) => {
      if (item?.type === 'gantt-chart') {
        return true;
      }

      return this.hasGanttChartWidget(item?.columns) || this.hasGanttChartWidget(item?.rows);
    });
  }
}
